/**
 * A small view of a map for the setup screens: the whole map built from its seed (one pixel per few cells), on its
 * sky, in its colours (themes.ts). A random choice shows a question mark: the map is drawn at the start.
 */
import { createScenario, isMapId, MAP_W, MAP_H } from '@invaders/game';
import { themeFor, themedColor } from './themes';

let lastKey = '';

export function drawMapPreview(canvas: HTMLCanvasElement, mapId: string, seed: number): void {
  const key = `${mapId} ${seed} ${canvas.width}`;
  if (key === lastKey && canvas.dataset.key === key) return;
  lastKey = key;
  canvas.dataset.key = key;
  const ctx = canvas.getContext('2d')!;
  const { width: w, height: h } = canvas;
  if (!isMapId(mapId)) {
    ctx.fillStyle = '#1a1c24';
    ctx.fillRect(0, 0, w, h);
    ctx.fillStyle = '#8a8d96';
    ctx.textAlign = 'center';
    ctx.font = '700 40px system-ui, sans-serif';
    ctx.fillText('?', w / 2, h / 2 + 6);
    ctx.font = '12px system-ui, sans-serif';
    ctx.fillText('map drawn at the start', w / 2, h / 2 + 28);
    return;
  }
  const theme = themeFor(mapId);
  const sky = ctx.createLinearGradient(0, 0, 0, h);
  for (const [at, color] of theme.sky) sky.addColorStop(at, color);
  ctx.fillStyle = sky;
  ctx.fillRect(0, 0, w, h);
  const { data } = createScenario({ terrain: mapId, seed, width: MAP_W, height: MAP_H }).world.physique;
  const image = ctx.getImageData(0, 0, w, h);
  const px = image.data;
  const colors = new Map<number, number>();
  for (let y = 0; y < h; y++) {
    const cy = Math.floor((y * MAP_H) / h);
    for (let x = 0; x < w; x++) {
      const m = data[cy * MAP_W + Math.floor((x * MAP_W) / w)];
      if (m === 0) continue;
      let c = colors.get(m);
      if (c === undefined) { c = themedColor(theme, m); colors.set(m, c); }
      const o = (y * w + x) * 4;
      px[o] = (c >> 16) & 0xff;
      px[o + 1] = (c >> 8) & 0xff;
      px[o + 2] = c & 0xff;
      px[o + 3] = 255;
    }
  }
  ctx.putImageData(image, 0, 0);
}
